/**
 * Cache invalidation utility for AllTuc
 * Handles invalidation of related cache entries when data changes
 */

import { cacheManager } from './cache';

export type CachePrefix =
  | 'product'
  | 'shop'
  | 'partner'
  | 'category'
  | 'user'
  | 'order'
  | 'review'
  | 'service'
  | 'booking'
  | 'content'
  | 'club';

export class CacheInvalidator {
  /**
   * Invalidate product-related caches
   */
  static invalidateProduct(productId: string): void {
    // Invalidate product itself
    cacheManager.invalidate('product', productId);
    
    // Invalidate related queries
    cacheManager.invalidate('product', 'active');
    cacheManager.invalidate('product', 'featured');
    cacheManager.invalidate('home', 'products');
  }

  /**
   * Invalidate shop-related caches
   */
  static invalidateShop(shopId: string): void {
    cacheManager.invalidate('shop', shopId);
    cacheManager.invalidate('shop', 'featured');
    cacheManager.invalidate('shop', 'active');
    cacheManager.invalidate('home', 'shops');
  }

  /**
   * Invalidate partner-related caches
   */
  static invalidatePartner(partnerId: string): void {
    cacheManager.invalidate('partner', partnerId);
  }

  /**
   * Invalidate category caches
   */
  static invalidateCategory(): void {
    cacheManager.invalidate('category', 'all');
  }

  /**
   * Invalidate user caches
   */
  static invalidateUser(userId: string): void {
    cacheManager.invalidate('user', userId);
  }

  /**
   * Invalidate order caches
   */
  static invalidateOrder(orderId: string): void {
    cacheManager.invalidate('order', orderId);
  }

  /**
   * Invalidate service caches
   */
  static invalidateService(serviceId: string): void {
    cacheManager.invalidate('service', serviceId);
  }

  /**
   * Invalidate booking caches
   */
  static invalidateBooking(bookingId: string): void {
    cacheManager.invalidate('booking', bookingId);
  }

  /**
   * Invalidate content caches (stories, posts, FAQ)
   */
  static invalidateContent(): void {
    cacheManager.invalidate('content', 'all');
  }

  /**
   * Invalidate club caches (drops, auctions, raffles)
   */
  static invalidateClub(): void {
    cacheManager.invalidate('club', 'all');
  }

  /**
   * Invalidate all caches (reset)
   */
  static invalidateAll(): void {
    cacheManager.clear();
  }

  /**
   * Invalidate multiple products
   */
  static invalidateProducts(productIds: string[]): void {
    productIds.forEach(id => this.invalidateProduct(id));
  }

  /**
   * Invalidate multiple shops
   */
  static invalidateShops(shopIds: string[]): void {
    shopIds.forEach(id => this.invalidateShop(id));
  }
}

export default CacheInvalidator;
